'use client';

import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Brain, Activity, TrendingUp, Globe } from 'lucide-react';

const conversations = [
    { from: 'Scout', to: 'Brain', message: 'Psychographic sync complete. 842 profiles analyzed. Identified 12 "Hyper-Motivated" founders ready for Archer.', result: 'Neural Sync', dot: 'bg-emerald-500' },
    { from: 'Brain', to: 'Scout', message: 'Social signal: #ScalingHacks trending. Pivot focus to Twitter "Growth" niche immediately.', result: 'Live Pivot', dot: 'bg-neutral-900' },
    { from: 'Quill', to: 'Brain', message: 'Generated 5 content variations using "Authority" framework. Ready for A/B deployment.', result: 'Content Ready', dot: 'bg-blue-500' },
    { from: 'Archer', to: 'Brain', message: 'Psychological outreach deployed to 50 leads. 28% reply rate. 4 discovery calls booked automatically.', result: '$48K pipeline', dot: 'bg-orange-500' },
    { from: 'Brain', to: 'Trader', message: '"Benefit-First" copy outperforming "Feature-Driven" by 3.2×. Reallocating budget now.', result: '3.2× ROI', dot: 'bg-neutral-900' },
];

const stats = [
    { icon: Activity, label: 'Actions today', value: '1,204' },
    { icon: Globe, label: 'Platforms synced', value: '12' },
    { icon: Brain, label: 'Agents active', value: '6' },
    { icon: TrendingUp, label: 'Patterns learned', value: '23' },
];

type Convo = (typeof conversations)[0] & { _id: string };

export const TheBrain = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [history, setHistory] = useState<Convo[]>([]);

    useEffect(() => {
        const t = setInterval(() => setActiveIndex(p => (p + 1) % conversations.length), 3200);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const next = conversations[activeIndex];
        setHistory(prev => [...prev.slice(-3), { ...next, _id: `${Date.now()}-${Math.random()}` }]);
    }, [activeIndex]);

    return (
        <section className="border-t border-neutral-100 py-24 bg-[#FAFAFA]">
            <div className="max-w-5xl mx-auto px-6">
                {/* Header — Linear-style numbered section */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-16"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-mono text-neutral-400">6.0</span>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral-600 uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 rounded-full bg-neutral-900" />
                            The Brain
                        </span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-5 tracking-tight leading-tight max-w-2xl">
                        One intelligence.<br />
                        <span className="text-neutral-400">Six agents. Zero micromanagement.</span>
                    </h2>
                    <p className="text-lg text-neutral-500 leading-relaxed max-w-2xl">
                        The Brain coordinates every agent in real time — reading signals, reallocating effort, and learning from every outcome. It&apos;s compounding intelligence that makes the whole smarter than its parts.
                    </p>
                </m.div>

                {/* Two-column: Live feed + Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Live agent conversation feed */}
                    <m.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="lg:col-span-3 rounded-2xl border border-neutral-200 bg-white overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.05)]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-neutral-900 flex items-center justify-center">
                                    <Brain size={14} className="text-white" />
                                </div>
                                <span className="text-sm font-semibold text-neutral-800">Neural Feed</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[11px] text-neutral-400 font-medium">Live</span>
                            </div>
                        </div>

                        {/* Feed */}
                        <div className="p-4 space-y-2.5 min-h-[280px]">
                            <AnimatePresence mode="popLayout">
                                {history.map((item) => (
                                    <m.div
                                        key={item._id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="flex items-start gap-3 p-3.5 rounded-xl bg-neutral-50 border border-neutral-100"
                                    >
                                        <div className="flex items-center gap-2 shrink-0 mt-0.5">
                                            <span className={`w-2 h-2 rounded-full ${item.dot} shrink-0`} />
                                            <span className="text-[11px] font-bold text-neutral-700 w-12">{item.from}</span>
                                        </div>
                                        <p className="text-[12px] text-neutral-600 leading-relaxed flex-1">{item.message}</p>
                                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full shrink-0 self-center">
                                            {item.result}
                                        </span>
                                    </m.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </m.div>

                    {/* Stat cards */}
                    <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
                        {stats.map((stat, i) => {
                            const Icon = stat.icon;
                            return (
                                <m.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: 0.15 + i * 0.07 }}
                                    className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
                                >
                                    <Icon size={18} className="text-neutral-400 mb-3" />
                                    <p className="text-2xl font-bold text-neutral-900 mb-1">{stat.value}</p>
                                    <p className="text-[11px] text-neutral-500 font-medium">{stat.label}</p>
                                </m.div>
                            );
                        })}

                        {/* Revenue card */}
                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.43 }}
                            className="col-span-2 rounded-2xl border border-orange-100 bg-orange-50 p-5"
                        >
                            <p className="text-[11px] font-semibold text-orange-600 uppercase tracking-widest mb-2">Attributed Revenue</p>
                            <p className="text-3xl font-bold text-neutral-900 mb-1">$48,200</p>
                            <p className="text-[12px] text-neutral-500">traced back to agent touchpoints this month</p>
                        </m.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
